import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import Clarity from '@microsoft/clarity'
import { getMe, logout as apiLogout, deleteAccount as apiDeleteAccount, getLoginUrl, AuthData, UserInfo } from '../services/authApi'
import { claimReferral } from '../services/referralApi'

interface AuthContextType {
  user: UserInfo | null
  credits: number
  subscription: AuthData['subscription']
  loading: boolean
  referralClaimed: boolean
  login: (provider: 'google' | 'microsoft', redirect?: string) => void
  logout: () => Promise<void>
  deleteAccount: () => Promise<void>
  refresh: () => Promise<void>
  dismissReferralClaimed: () => void
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  credits: 0,
  subscription: null,
  loading: true,
  referralClaimed: false,
  login: () => {},
  logout: async () => {},
  deleteAccount: async () => {},
  refresh: async () => {},
  dismissReferralClaimed: () => {},
})

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserInfo | null>(null)
  const [credits, setCredits] = useState(0)
  const [subscription, setSubscription] = useState<AuthData['subscription']>(null)
  const [loading, setLoading] = useState(true)
  const [referralClaimed, setReferralClaimed] = useState(false)

  const dismissReferralClaimed = useCallback(() => setReferralClaimed(false), [])

  const refresh = useCallback(async () => {
    try {
      const data = await getMe()
      if (data) {
        setUser(data.user)
        setCredits(data.credits.available)
        setSubscription(data.subscription)

        // Identify user in Clarity for session tracking
        try { Clarity.identify(data.user.id, undefined, undefined, data.user.displayName) } catch {}

        // Claim pending referral if exists
        const pendingRef = localStorage.getItem('pendingReferral')
        if (pendingRef) {
          localStorage.removeItem('pendingReferral')
          claimReferral(pendingRef).then(claimed => {
            if (claimed) {
              setReferralClaimed(true)
              // Re-fetch credits since they changed
              getMe().then(d => { if (d) setCredits(d.credits.available) })
            }
          })
        }
      } else {
        setUser(null)
        setCredits(0)
        setSubscription(null)
      }
    } catch {
      setUser(null)
      setCredits(0)
      setSubscription(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const login = useCallback((provider: 'google' | 'microsoft', redirect?: string) => {
    window.location.href = getLoginUrl(provider, redirect)
  }, [])

  const logout = useCallback(async () => {
    await apiLogout()
    setUser(null)
    setCredits(0)
    setSubscription(null)
  }, [])

  const deleteAccountFn = useCallback(async () => {
    await apiDeleteAccount()
    setUser(null)
    setCredits(0)
    setSubscription(null)
  }, [])

  return (
    <AuthContext.Provider value={{ user, credits, subscription, loading, referralClaimed, login, logout, deleteAccount: deleteAccountFn, refresh, dismissReferralClaimed }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
